import React, { Component } from 'react';
import { connect } from 'react-redux';
import { animateScroll as scroll } from 'react-scroll/modules';
import { bindActionCreators, Dispatch } from 'redux';

import { fetchApplication, FormTypeFeedback, sendFeedback } from '../../actions';
import { DispatchWithState, StringKeyValue } from '../../actions/types';
import Loader from '../../components/Loader/Loader';
import { AppState } from '../../reducers';
import { ApplicationReducer } from '../../reducers/application';
import { CalculatorReducer } from '../../reducers/calculator';
import { CompletionReducer } from '../../reducers/completion';
import { LoaderReducer } from '../../reducers/loader';
import ApplicationStatus from '../../util/mappings/applicationStatus';
import LoanType from '../../util/mappings/loanType';
import Control from './Control';
import LoanApproved from './LoanApproved';
import LoanCommitmentApproved from './LoanCommitmentApproved';
import Rejected from './Rejected';

interface Props {
    action: {
        fetchApplication: () => DispatchWithState;
        sendFeedback: (form: FormTypeFeedback) => DispatchWithState;
    };
    application: ApplicationReducer;
    completion: CompletionReducer;
    calculator: CalculatorReducer;
    loader: LoaderReducer;
    text: StringKeyValue;
}

class LoanApplicationReceipt extends Component<Props> {
    private statusPollingInterval: number | null = null;

    private hasValidReceiptStatus = (applicationStatus?: string): boolean =>
        [ApplicationStatus.approved, ApplicationStatus.control, ApplicationStatus.rejected].includes(
            applicationStatus || '',
        );

    private startStatusPolling = (): void => {
        if (this.statusPollingInterval !== null) {
            return;
        }

        this.statusPollingInterval = window.setInterval(() => {
            const { completion, loader, action } = this.props;
            const { applicationStatus } = completion.ongoing;

            if (this.hasValidReceiptStatus(applicationStatus)) {
                this.stopStatusPolling();
                return;
            }

            if (!loader.isFetchingApplication) {
                action.fetchApplication();
            }
        }, 2000);
    };

    private stopStatusPolling = (): void => {
        if (this.statusPollingInterval !== null) {
            window.clearInterval(this.statusPollingInterval);
            this.statusPollingInterval = null;
        }
    };

    private updateStatusPolling = (): void => {
        const { applicationStatus } = this.props.completion.ongoing;

        if (!this.hasValidReceiptStatus(applicationStatus)) {
            this.startStatusPolling();
            return;
        }

        this.stopStatusPolling();
    };

    componentDidMount() {
        const { loader, action } = this.props;
        if (!loader.hasFetchedApplication && !loader.isFetchingApplication) action.fetchApplication();
        this.updateStatusPolling();
        scroll.scrollToTop();
    }

    componentDidUpdate(prevProps: Props) {
        const prevStatus = prevProps.completion.ongoing.applicationStatus;
        const currentStatus = this.props.completion.ongoing.applicationStatus;
        const isFetchStateChanged = prevProps.loader.isFetchingApplication !== this.props.loader.isFetchingApplication;

        if (prevStatus !== currentStatus || isFetchStateChanged) {
            this.updateStatusPolling();
        }
    }

    componentWillUnmount() {
        this.stopStatusPolling();
    }

    render() {
        const { completion, action, loader, calculator, application, text } = this.props;
        const { applicationStatus } = completion.ongoing;
        const { loanType } = calculator.loanInfo;
        const isValidReceiptStatus = this.hasValidReceiptStatus(applicationStatus);

        if (!isValidReceiptStatus) {
            return <Loader title={text.loadingApplicationStatus || text.loadingDefault} />;
        }

        return (
            <div className="receipt">
                <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                    <>
                        {applicationStatus === ApplicationStatus.approved && (
                            <>
                                {loanType === LoanType.loan && <LoanApproved completion={completion} text={text} />}
                                {loanType === LoanType.loanCommitment && (
                                    <LoanCommitmentApproved
                                        completion={completion}
                                        calculator={calculator}
                                        text={text}
                                    />
                                )}
                            </>
                        )}
                        {applicationStatus === ApplicationStatus.control && (
                            <Control completion={completion} action={action} loader={loader} text={text} />
                        )}
                        {applicationStatus === ApplicationStatus.rejected && (
                            <Rejected completion={completion} text={text} creditFrozen={application.creditFrozen} />
                        )}
                    </>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    application: state.application,
    completion: state.completion,
    calculator: state.calculator,
    loader: state.loader,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            fetchApplication,
            sendFeedback,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoanApplicationReceipt);
